import { SALE_TRANSACTION_FINISHED } from "../../../_Bootstrap/GlobalConstants";
import PopupManager from "../../../Common/Popups/PopupManager";
import { type LocalDatabaseCustomer } from "../../../Data/_Bases/BaseCustomerRepository";
import { type LocalDatabasePromotion } from "../../../Data/_Bases/BasePromotionRepository";
import { type SaleProduct } from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import { ApplicationEventsToShopfront } from "../../Platform/Applications/ApplicationEvents";
import type { ApplicationManagerEventData } from "../../Platform/Applications/ApplicationManager";
import { getApplicationManager } from "../../Platform/Applications/ApplicationUtilities";
import { getLogLevel, LogLevel } from "../../Platform/Logging";
import { getOption } from "../../Platform/Options/Options";
import { PromotionAgentCommunicator } from "./PromotionAgentCommunicator";
import { PROMOTION_EVENTS, PromotionCalculationInvalid } from "./PromotionEvents";

export const RECALCULATE_PROMOTIONS = "shopfront:promotions:recalculate";

export class PromotionAgent {
    protected calculatingIndex: number;
    public communicator: PromotionAgentCommunicator;
    protected applicationPromotions = new Set<string>();
    protected static currentPromotionAgent: undefined | PromotionAgent;

    constructor() {
        this.calculate = this.calculate.bind(this);

        PromotionAgent.currentPromotionAgent = this;

        this.calculatingIndex = 0;
        this.communicator = new PromotionAgentCommunicator();

        if(process.env.NODE_ENV !== "test") {
            getApplicationManager().addEventListener(
                ApplicationEventsToShopfront.LISTENERS.SELL_SCREEN_PROMOTION_APPLICABLE,
                this.handleApplicablePromotionsChange
            );
        }

        if(typeof document !== "undefined") {
            document.addEventListener(SALE_TRANSACTION_FINISHED, this.handleTransactionFinished);
        }
    }

    /**
     * Inform the communicator that the transaction is finished
     */
    protected handleTransactionFinished = (): void => {
        void this.communicator.sendMessage(PROMOTION_EVENTS.TRANSACTION_FINISHED);
    };

    /**
     * Check if the response is an error
     * @param response
     * @returns {response is {error: true, object: Error}}
     * @protected
     */
    protected isErrorResponse(
        response: any
    ): response is { error: true; object: Error; isInvalidCalculation?: boolean; } {
        return response.error === true;
    }

    /**
     * Calculates the prices of products provided, If formatFunction is provided
     * it will call formatFunction with the new products as the first argument
     * otherwise it will call the set formatFunction
     * @param products
     * @param customer
     * @param formatFunction
     * @returns {*}
     */
    public async calculate(
        products: Array<SaleProduct>,
        customer: LocalDatabaseCustomer | null,
        formatFunction: (products: Array<any>) => void
    ): Promise<void> {
        this.calculatingIndex++;

        if(!process.env.CI && getLogLevel() >= LogLevel.VERBOSE && this.calculatingIndex > 1) {
            console.info("Starting promotion calculation, the current index is now ", this.calculatingIndex);
        }

        return new Promise(res => {
            this.communicator.sendMessage(PROMOTION_EVENTS.CALCULATE, {
                products,
                customer,
                outlet               : getOption("outlet"),
                costCalculationMethod: getOption("cost_calculation_method"),
                useGlobalCost        : getOption("outlet_use_global_cost"),
                applicable           : Array.from(this.applicationPromotions),
            }, res);
        })
            .then((results: {
                usedGreedyCalculation: boolean;
                products: Array<SaleProduct>;
            } | {
                error: true;
                object: Error;
                isInvalidCalculation?: boolean;
            }) => {
                this.calculatingIndex--;

                if(this.calculatingIndex < 0) {
                    this.calculatingIndex = 0;
                }

                if(!process.env.CI && getLogLevel() >= LogLevel.VERBOSE && this.calculatingIndex > 0) {
                    console.info("Finished promotion calculation, the current index is now ", this.calculatingIndex);
                }

                if(this.isErrorResponse(results)) {
                    if(results.object instanceof PromotionCalculationInvalid || results.isInvalidCalculation) {
                        // Promotion calculation result is now invalid
                        return;
                    }

                    throw results.object;
                }

                if(results.usedGreedyCalculation) {
                    PopupManager.warning(
                        "We've detected a exceptionally large quantity so we've approximated the price calculation" +
                        " please check to ensure the prices are correct"
                    );
                }

                if(typeof formatFunction === "function") {
                    formatFunction(results.products);
                }
            })
            .catch(err => {
                this.calculatingIndex--;
                console.error(
                    "Received an error while performing the promotion calculation, the index is now",
                    this.calculatingIndex
                );
                throw err;
            });
    }

    public everydayPrice(id: string, quantity: number, overridePriceSetId?: string): Promise<number> {
        return new Promise(res => {
            this.communicator.sendMessage(PROMOTION_EVENTS.EVERYDAY_PRICE, {
                id,
                quantity,
                overridePriceSetId,
            }, res);
        });
    }

    public calculateLoyalty(
        products: Array<SaleProduct>,
        customer: LocalDatabaseCustomer | null = null
    ): Promise<Array<SaleProduct>> {
        return new Promise(res => {
            this.communicator.sendMessage(PROMOTION_EVENTS.CALCULATE_LOYALTY, {
                products,
                customer,
            }, res);
        });
    }

    public setupDataSource = (): void => {
        this.communicator.sendMessage(PROMOTION_EVENTS.SETUP_DATA_SOURCES);
    };

    public isCurrentlyCalculating(): boolean {
        return this.calculatingIndex > 0;
    }

    public simulate(
        originalId: null | string,
        promotion: null | LocalDatabasePromotion
    ): Promise<void> {
        return new Promise(res => {
            this.communicator.sendMessage(PROMOTION_EVENTS.SIMULATE, {
                promotion,
                originalId,
            }, res);
        });
    }

    public enableActivationKey(key: string): void {
        this.applicationPromotions.add(key);
    }

    public disableActivationKey(key: string): void {
        this.applicationPromotions.delete(key);
    }

    protected handleApplicablePromotionsChange = (data: ApplicationManagerEventData<{
        key: string;
        enable: boolean;
    }>): void => {
        const currentLength = this.applicationPromotions.size;

        if(data.data.enable) {
            this.enableActivationKey(data.data.key);
        } else {
            this.disableActivationKey(data.data.key);
        }

        if(currentLength !== this.applicationPromotions.size) {
            document.dispatchEvent(new CustomEvent(RECALCULATE_PROMOTIONS));
        }
    };

    public static getAgent(): PromotionAgent {
        return PromotionAgent.currentPromotionAgent ?? new PromotionAgent();
    }
}

let agent: PromotionAgent;

if(process.env.NODE_ENV !== "test") {
    agent = new PromotionAgent();
}

export default agent!;
